import { Injectable } from '@angular/core';
import { ApiClientConstant } from '@cureskin/api-client';
import { InstantCheckup, RegimenClass } from '@cureskin/api-client/src/server-api';

/** One regimen class's required-photo state, rendered as a single "Complete Your Profile" tab. */
export interface CompleteProfileSection {
  regimenClass: RegimenClass;
  checkupsByType: Record<string, InstantCheckup | null>;
}

export enum CompleteProfilePhotoState {
  NONE = 'NONE',
  PARTIAL = 'PARTIAL',
  FULL = 'FULL',
}

export enum CompleteProfileBanner {
  REBRANDED = 'REBRANDED',
  PROFILE = 'PROFILE',
  NONE = 'NONE',
}

export enum CompleteProfilePosition {
  TOP = 'TOP',
  BOTTOM = 'BOTTOM',
}

export interface CompleteProfileClassContext {
  regimenClass: RegimenClass;
  isChatPending: boolean;
  chatStartedAt: number | null;
  hasRegimen: boolean;
  regimenCreatedAt: number | null;
}

export interface CompleteProfileDecision {
  banner: CompleteProfileBanner;
  cards: RegimenClass[];
  position: CompleteProfilePosition;
  sections: CompleteProfileSection[];
}

@Injectable({ providedIn: 'root' })
export class CompleteYourProfileService {

  getProfileClasses(): RegimenClass[] {
    return [ApiClientConstant.Regimen.Class.FACE, ApiClientConstant.Regimen.Class.HAIR];
  }

  /**
   */
  decide(
    sections: CompleteProfileSection[],
    contexts: CompleteProfileClassContext[],
  ): CompleteProfileDecision {
    const empty = sections.filter(
      (section: CompleteProfileSection): boolean => this.photoState(section) === CompleteProfilePhotoState.NONE);
    const hasPartial = sections.some(
      (section: CompleteProfileSection): boolean => this.photoState(section) === CompleteProfilePhotoState.PARTIAL);

    if (hasPartial) {
      return {
        banner: CompleteProfileBanner.PROFILE,
        cards: [],
        position: CompleteProfilePosition.BOTTOM,
        sections: this.orderSections(sections, contexts),
      };
    }
    if (!empty.length) {
      return {
        banner: CompleteProfileBanner.NONE,
        cards: [],
        position: CompleteProfilePosition.BOTTOM,
        sections: [],
      };
    }

    const candidates = empty.map((section: CompleteProfileSection): RegimenClass => section.regimenClass);
    const qualified = candidates.filter((regimenClass: RegimenClass): boolean =>
      this.isQualified(this.contextOf(contexts, regimenClass)));

    return {
      banner: CompleteProfileBanner.REBRANDED,
      cards: qualified.length ? qualified : candidates.slice(0, 1),
      position: qualified.length ? CompleteProfilePosition.TOP : CompleteProfilePosition.BOTTOM,
      sections: [],
    };
  }

  private photoState(section: CompleteProfileSection): CompleteProfilePhotoState {
    const checkups = Object.values(section.checkupsByType ?? {});
    if (!checkups.length) return CompleteProfilePhotoState.FULL;
    const uploaded = checkups.filter((checkup: InstantCheckup | null): boolean => !!checkup).length;
    if (!uploaded) return CompleteProfilePhotoState.NONE;
    return uploaded === checkups.length ? CompleteProfilePhotoState.FULL : CompleteProfilePhotoState.PARTIAL;
  }

  private isQualified(context: CompleteProfileClassContext | undefined): boolean {
    return !!context && (context.isChatPending || context.hasRegimen);
  }

  private contextOf(
    contexts: CompleteProfileClassContext[], regimenClass: RegimenClass,
  ): CompleteProfileClassContext | undefined {
    return contexts.find((context: CompleteProfileClassContext): boolean => context.regimenClass === regimenClass);
  }

  private orderSections(
    sections: CompleteProfileSection[], contexts: CompleteProfileClassContext[],
  ): CompleteProfileSection[] {
    const rank = (section: CompleteProfileSection): number =>
      (this.photoState(section) === CompleteProfilePhotoState.PARTIAL ? 0 : 1);

    return [...sections].sort((a: CompleteProfileSection, b: CompleteProfileSection): number =>
      rank(a) - rank(b)
      || this.compareStart(this.contextOf(contexts, a.regimenClass), this.contextOf(contexts, b.regimenClass)));
  }

  private compareStart(
    a: CompleteProfileClassContext | undefined, b: CompleteProfileClassContext | undefined,
  ): number {
    const aPending = !!a?.isChatPending;
    const bPending = !!b?.isChatPending;
    if (aPending !== bPending) return aPending ? -1 : 1;
    if (aPending) return this.compareTime(a?.chatStartedAt ?? null, b?.chatStartedAt ?? null);
    return this.compareTime(a?.regimenCreatedAt ?? null, b?.regimenCreatedAt ?? null);
  }

  private compareTime(a: number | null, b: number | null): number {
    if (a === b) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return a - b;
  }
}
